import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { AddMenuItems } from './AddMenuItems';
import { EventPill } from './EventPill';

/**
 * WeekGrid — 7-column week view. Same interaction model as MonthGrid
 * but with vertical event stacks per day (no max-3 clipping).
 */
export const WeekGrid = ({
  currentDate,
  getEventsForDate,
  propertyColorMap,
  dragOverDate,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragStart,
  onDragEnd,
  onEventClick,
  onCellClick,
  onDayPickType,
  showPropertyName,
}) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const days = [];
  for (let i = 0; i < 7; i++) days.push(addDays(weekStart, i));

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      <div className="grid grid-cols-7 divide-x divide-slate-200">
        {days.map((d) => {
          const dayEvents = getEventsForDate(d);
          const isToday = isSameDay(d, new Date());
          const dateStr = format(d, 'yyyy-MM-dd');
          const isDragOver = dragOverDate === dateStr;

          return (
            <DropdownMenu key={dateStr}>
              <div
                className={`min-h-[400px] ${isDragOver ? 'bg-blue-50' : ''}`}
                onDragOver={(e) => onDragOver(e, d)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, d)}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    onClick={() => onCellClick(d)}
                    className={`w-full p-4 border-b border-slate-200 text-center hover:bg-slate-100 transition-colors ${
                      isToday ? 'bg-blue-50' : 'bg-slate-50'
                    }`}
                  >
                    <p className="text-xs text-slate-500 uppercase">{format(d, 'EEE')}</p>
                    <p
                      className={`text-2xl font-bold mt-1 ${isToday ? 'text-blue-600' : 'text-slate-900'}`}
                    >
                      {format(d, 'd')}
                    </p>
                  </button>
                </DropdownMenuTrigger>
                <div className="p-2 space-y-2">
                  {dayEvents.map((event) => (
                    <EventPill
                      key={`${event.type}-${event.id}`}
                      event={event}
                      propertyColorMap={propertyColorMap}
                      onEventClick={onEventClick}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                      showPropertyName={showPropertyName}
                      variant="week"
                      testIdPrefix="week-event"
                    />
                  ))}
                </div>
              </div>
              <DropdownMenuContent align="start">
                <AddMenuItems label onPick={(type) => onDayPickType(d, type)} />
              </DropdownMenuContent>
            </DropdownMenu>
          );
        })}
      </div>
    </div>
  );
};

export default WeekGrid;
